import { ApiService } from '../shared/services/api-service/api.service';
import { IPalsPayload, IPalsResponse, IPalsRequestPayloadOptions } from './interfaces/pals.interfaces';
import {
    SORT_ORDER_PREFERENCE_ENUM, ROOM_RATE_OR_PACKAGE_SORT_PREFERENCE_PAYLOAD_VALUES_ENUM,
    RATE_PLAN_TYPES_ENUM, TPA_EXTENSION_ENUM
} from './constants/pals.constants';
import { CommonService } from '../shared/services/common-service/common.service';

declare const TCCommonService;

class PalsServiceClass {
    private _dataStoredInVar = [];

    async getAvailability(hotelCode: number, requestPayloadOptions: IPalsRequestPayloadOptions,
        enableApiCaching: boolean = false, tcShareLocation: boolean = false, currencyCode: string = null) {
        const shopRequestPayload = this._buildShopRequestPayload(requestPayloadOptions, tcShareLocation, currencyCode);
        const cacheKey = 'be_' + TCCommonService.ConfigService.getPlatformType() + '_wc_avail_' + hotelCode + '_' +
            CommonService.generateHashCode(JSON.stringify(shopRequestPayload));
        if (enableApiCaching && this._dataStoredInVar[cacheKey]) {
            /* This has to be move into tc-comp-service to make it generic. Instead we have to pass one variable
             and based on that need to save object in session storage or in datastore object */
            return this._dataStoredInVar[cacheKey];
            /* const palsDataFromCache = TCCommonService.BrowserStorageService.getBrowserStorage({
                key: cacheKey,
                expiryTime: CacheExpiryForARIApi
            });
            if (palsDataFromCache) {
                return palsDataFromCache;
            } */
        }
        try {
            delete shopRequestPayload['roomSequenceNumber'];
            const palsApiResponse: Response = await ApiService.callPalsApi(hotelCode, shopRequestPayload);
            const palsResponseObject: IPalsResponse = await palsApiResponse.json();
            if (enableApiCaching) {
                this._dataStoredInVar[cacheKey] = palsResponseObject;
                // TCCommonService.BrowserStorageService.setBrowserStorage({ key: cacheKey, value: palsResponseObject });
            }
            return palsResponseObject;
        } catch (errorResponse) {
            throw errorResponse;
        }
    }
    /**
    * function to build Shop RequestPayload
    * @param shopRequestPayload
    * @returns palsPayload
    * @memberof PalsResponseService
    */
    private _buildShopRequestPayload(requestPayloadOptions: IPalsRequestPayloadOptions,
        tcShareLocation: boolean = true, currencyCode: string = null): IPalsPayload {
        let sortingPreference = null;
        if (requestPayloadOptions.sortOrderPreference &&
            requestPayloadOptions.sortOrderPreference.toLowerCase() ===
            SORT_ORDER_PREFERENCE_ENUM.SORT_BY_ORDER) {
            sortingPreference = ROOM_RATE_OR_PACKAGE_SORT_PREFERENCE_PAYLOAD_VALUES_ENUM.SORT_BY_ORDER;
        } else if (requestPayloadOptions.sortOrderPreference &&
            requestPayloadOptions.sortOrderPreference.toLowerCase() ===
            SORT_ORDER_PREFERENCE_ENUM.AVAILABLE_FIRST) {
            sortingPreference = ROOM_RATE_OR_PACKAGE_SORT_PREFERENCE_PAYLOAD_VALUES_ENUM.AVAILABLE_FIRST;
        } else {
            sortingPreference = ROOM_RATE_OR_PACKAGE_SORT_PREFERENCE_PAYLOAD_VALUES_ENUM.SORT_BY_ORDER;
        }
        let searchCriteriaType = null;
        if (requestPayloadOptions.ratePlanType?.toLowerCase() === RATE_PLAN_TYPES_ENUM.PACKAGE) {
            searchCriteriaType = (RATE_PLAN_TYPES_ENUM.PACKAGE).charAt(0).toUpperCase() + (RATE_PLAN_TYPES_ENUM.PACKAGE).slice(1);
        } else if (requestPayloadOptions.ratePlanType?.toLowerCase() === RATE_PLAN_TYPES_ENUM.REGULAR) {
            searchCriteriaType = (RATE_PLAN_TYPES_ENUM.REGULAR).charAt(0).toUpperCase() + (RATE_PLAN_TYPES_ENUM.REGULAR).slice(1);
        }
        const palsPayload: IPalsPayload = {
            roomStay: {
                endDate: requestPayloadOptions.dateOut,
                guestCount: {
                    adults: requestPayloadOptions.adults,
                    infants: requestPayloadOptions.infants,
                    children: {
                        ages: (requestPayloadOptions.childAges && requestPayloadOptions.childAges.length > 0)
                            ? requestPayloadOptions.childAges.sort() : null,
                        count: requestPayloadOptions.children
                    }
                },
                roomQuantity: requestPayloadOptions.roomCount,
                startDate: requestPayloadOptions.dateIn,
                productSearchCriteria: {
                    sortingPreference: sortingPreference,
                    includeUnavailable: requestPayloadOptions.includeUnavailable
                }
            }
        };
        if (searchCriteriaType) {
            palsPayload.roomStay.productSearchCriteria.type = searchCriteriaType;
        }
        if (requestPayloadOptions.languageCode) {
            palsPayload.languageCode = requestPayloadOptions.languageCode;
        }
        if (requestPayloadOptions.reservationCode) {
            palsPayload.reservationCode = requestPayloadOptions.reservationCode;
        }
        if (requestPayloadOptions.accessCodes && requestPayloadOptions.accessCodes.length) {
            palsPayload.accessCodes = requestPayloadOptions.accessCodes;
        }
        if (requestPayloadOptions.roomTypeId) {
            palsPayload.roomStay.productSearchCriteria.roomTypeId = requestPayloadOptions.roomTypeId;
        }
        if (requestPayloadOptions.productId) {
            palsPayload.roomStay.productSearchCriteria.productId = requestPayloadOptions.productId;
        }
        if (requestPayloadOptions.bookerIdentifier || requestPayloadOptions.partnerIdentifier) {
            palsPayload.partnerIdentifier = requestPayloadOptions.partnerIdentifier ? requestPayloadOptions.partnerIdentifier : '';
            palsPayload.bookerIdentifier = requestPayloadOptions.bookerIdentifier ? requestPayloadOptions.bookerIdentifier : '';
        }
        if (requestPayloadOptions.promotionId) {
            palsPayload.promotionId = requestPayloadOptions.promotionId;
        }
        if (!tcShareLocation) {
            palsPayload.disableLocaitonSharing = true;
        } else {
            palsPayload.disableLocaitonSharing = false;
        }
        if (currencyCode) {
            palsPayload.currencyCode = currencyCode;
        }
        palsPayload.tpaExtension = [];
        if (requestPayloadOptions.loyaltyTierId) {
            palsPayload.tpaExtension.push({
                name: TPA_EXTENSION_ENUM.LOYALTY_TIER_ID,
                value: requestPayloadOptions.loyaltyTierId.toString()
            });
        }
        if (requestPayloadOptions.includeMemberRate) {
            palsPayload['includeMemberRate'] = true;
        }
        palsPayload['roomSequenceNumber'] = String(requestPayloadOptions.roomSequenceNumber);
        palsPayload['includeNightlyRates'] = true;
        return palsPayload;
    }
}

export const PalsService = new PalsServiceClass();
